/*
 * Project container component specific for Adalia M&E.
 *
 * This component expects related projects as a prop
 *
 */
import React from "react";
import {
  DataType,
} from "../common.jsx";
import Helpers from './helpers.jsx';
import Setting from './setting.jsx';

import Resolver from '../component_resolver.jsx';
import RadioSelection from './radio_selection.jsx'
import Text from './text.jsx';
import GridLayout from './grid_layout.jsx';
import RepeatableRegion from './repeatable_region.jsx';

import { resolveComponentProps } from "../form_viewer.jsx";
import Tooltip from "../../common/Tooltip.jsx";

export class AdaliaMEProjectInformation extends Text {
  static get TYPE() {
    return "adalia_me_project_information";
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      cloneable: false,
    });
  }

  renderView() {
    const { project = {} } = this.props;

    const displayStatus = ["approved", "rejected"].includes(project.report_status);
    const statusUpdatedBy = project.status_updated_by;
    const statusUpdatedAt = project.status_updated_at
      ? DT.dt(project.status_updated_at, {datetime: true})
      : "";
    const statusLabel = project.report_approved ? I18n.t(`project_report_current_status_${project.report_status}`) : I18n.t(`project_report_status_${project.report_status}`)
    const tooltipTitle = statusUpdatedBy ? I18n.t("project_report_status_changed_tooltip", { status_changed_by: statusUpdatedBy, status_changed_at: statusUpdatedAt }) : `${statusLabel} - ${statusUpdatedAt}`;

    return (
      <div>
        {this.inEditMode ? "<PROJECT IDENTIFIER> <Project Title>" : project.label}
        {displayStatus ? (
          <Tooltip title={tooltipTitle} options={{ placement: "right", container: "body" }}>
            <span className={`label ml-1 mr-1 ${project.report_status}`}>{statusLabel}</span>
          </Tooltip>
        ) : null}
      </div>
    );
  }

  renderSettings() {
    return null;
  }
}

export default class AdaliaMERepeatableProjectRegion extends RepeatableRegion {
  static get TYPE() {
    return "adalia_me_repeatable_project_region";
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      component: {
        ...GridLayout.defaultContent(language),
        rows: [
          [{ span: 1, component: AdaliaMEProjectInformation.defaultContent(language) }],
          [
            { span: 1, component: null },
            { span: 1, component: null },
          ],
        ],
      },
    });
  }

  constructor(props) {
    super(props);

    let rowCount = props.projects?.length || 1;
    this.projects = [];

    if (!this.inEditMode) {
      const answer = (this.props.answers || {})[(this.props.content || {}).name] || {};

      this.projects =
        Object.keys(answer).length == 0
          ? props.projects || []
          : Object.keys(answer)
              .filter((k) => k != "row_count")
              .sort((a, b) => parseInt(a.split("-")[1]) - parseInt(b.split("-")[1]))
              .map((k) => (props.projects || []).find((p) => p.id == (answer[k] || {}).project_id))
              .filter(Boolean)

      this.answers = {'row_count':this.projects.length}
      this.projects.forEach((p,i) => {
        this.answers['row-' + i] = Object.values(answer).filter((a) => a.project_id == p.id)[0]
      })

      this.updateAnswer(this.answers)
      
      // If there are answers without project_id, we'll just assume the order of the projects is the same
      // This can happen when answers have not been saved and user navigates back to the form with the browsers's back button
      if (!this.projects.filter(Boolean).length && props.projects?.length) {
        this.projects = props.projects;
      }

      rowCount = this.projects.length;
    }

    let rows = [];

    for (let i = 0; i < rowCount; i++) {
      rows.push("row-" + i);
    }

    this.state = {
      ...this.state,
      rows,
    };
  }

  renderView() {
    let rows = [];

    this.state.rows.forEach((rowId, i) => {
      let component = null;
      const project = this.projects[i];

      // Skip row if projects is defined but this row doesn't have a project
      if (!project && this.projects.length) return;

      if (this.props.content.component) {
        component = Resolver.createElement(
          this.props.content.component.type,
          resolveComponentProps(this.props.content.component, this.props, {
            selectCallback: this.props.selectCallback,
            selectedId: this.props.selectedId,
            repeatedFieldScope: this.props.content.name + "[row-" + i + "]",
            repeatedFieldRowIndex: i,
            project: project,
            readOnly: project?.report_approved,
            // Provide only prefill answers relevant for project
            prefillAnswers: !project?.report_approved
              ? (this.props.prefillAnswers || {})[project?.id] || {}
              : {},
            prefillAttachments: !project?.report_approved
              ? (this.props.prefillAttachments || {})[project?.id] || {}
              : {},
          })
        );
      } else if (this.inEditMode) {
        component = <Helpers.DropArea componentId={this.props.content.id} />;
      }
      rows.push(
        <div key={rowId} className="component-row">
          <div className="component">{component}</div>
          {project ? (
            <input
              type="hidden"
              name={`${this.fullFieldName()}[row-${i}][project_id]`}
              value={project.id}
            />
          ) : null}
        </div>
      );
    });

    return (
      <div>
        <label>{this.getText("label")}</label>
        {rows}
        <input
          type="hidden"
          name={this.fullFieldName() + "[row_count]"}
          defaultValue={rows.length}
        />
      </div>
    );
  }

  renderSettings() {
    return (
      <div>
        <Setting.TextField
          name="label"
          label={I18n.t('field_label', {scope: 'fe'})}
          value={this.getText("label")}
          onChange={this.updateSettings}
        />
        <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          onChange={this.updateSettings}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
        />
      </div>
    );
  }
}

export class AdaliaMERepeatableProjectRegionReview extends AdaliaMERepeatableProjectRegion {
  static get TYPE() {
    return "adalia_me_repeatable_project_region_review";
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      component: {
        ...GridLayout.defaultContent(language),
        rows: [
          [
            { span: 1, component: AdaliaMEProjectInformation.defaultContent(language) },
            { span: 1, component: AdaliaMEProjectRadioSelection.defaultContent(language) },
          ],
        ],
      },
    });
  }
}

export class AdaliaMEProjectRadioSelection extends RadioSelection {
  static get TYPE() {
    return "adalia_me_project_radio_selection";
  }

  static get OPTIONS() {
    return [
      { value: "approved", label: "opt-approved" },
      { value: "rejected", label: "opt-rejected" },
    ];
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      name: "project_selection",
      options: [], // Static options
      required: true,
      deletable: true,
      cloneable: false,
      lockOptionValues: true,
      dataType: DataType.STRING,
      translations: {},
    });
  }

  getOptions() {
    return this.constructor.OPTIONS;
  }

  getText(key) {
    return this.getOptions()
      .map((o) => o.label)
      .includes(key)
      ? I18n.t(`project_selection_${key}`, { scope: "fe" })
      : super.getText(key);
  }

  renderSettings() {
    return (
      <div ref="settings">
        <Setting.TextField
          name="label"
          label={I18n.t('field_label', {scope: 'fe'})}
          value={this.getText("label")}
          onChange={this.updateSettings}
          description={
            this.props.content.lockOptionValues
              ? I18n.t('locked_field_label_description', {scope: 'fe'})
              : ""
          }
        />
        <Setting.LongTextField
          name="description"
          label={I18n.t('field_description', {scope: 'fe'})}
          value={this.getText("description")}
          onChange={this.updateSettings}
        />
        <Setting.LongTextField
          name="help"
          label={I18n.t('field_help', {scope: 'fe'})}
          value={this.getText("help")}
          onChange={this.updateSettings}
        />
        <Setting.TextField
          name="reportKey"
          label={I18n.t('field_report_key', {scope: 'fe'})}
          value={this.props.content.reportKey}
          onChange={this.updateSettings}
          description={I18n.t('field_report_key_description', {scope: 'fe'})}
        />
      </div>
    );
  }

  beforeUpdateSettings(content) {
    content.required = true;
    super.beforeUpdateSettings(content);
  }
}